/**
 * 로그인 계정 잠금 판별.
 * API 스펙 확정 전에도 다중 응답 형태를 수용할 수 있게 준비한다.
 *
 * 인식 예:
 * - code / errorCode: ACCOUNT_LOCKED | USER_LOCKED | LOCKED
 * - locked | accountLocked: true
 * - status: LOCKED
 * - message: 「잠김」「잠금」「5회」「locked」 등
 */

export const LOGIN_ACCOUNT_LOCKED_CODES = [
  'ACCOUNT_LOCKED',
  'USER_LOCKED',
  'LOCKED',
] as const;

export type LoginAccountLockedCode =
  (typeof LOGIN_ACCOUNT_LOCKED_CODES)[number];

/** Figma「로그인 — 계정 잠금」알림 문구 */
export const LOGIN_ACCOUNT_LOCKED_TITLE =
  '비밀번호 5회 오류로 계정이 잠겼습니다.';
export const LOGIN_ACCOUNT_LOCKED_HINT =
  '마스터 또는 관리 계정에 잠금 해제를 요청하세요.';

type LoginErrorBody = {
  code?: unknown;
  errorCode?: unknown;
  error?: unknown;
  locked?: unknown;
  accountLocked?: unknown;
  status?: unknown;
  message?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const normalizeCode = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  return value.trim().toUpperCase();
};

const isLockedCode = (code: string | null): boolean =>
  !!code &&
  (LOGIN_ACCOUNT_LOCKED_CODES as readonly string[]).includes(code);

/** 메시지 휴리스틱 — 코드가 없을 때 서버 문구로 잠금 추정 */
export const looksLikeAccountLockedMessage = (message: unknown): boolean => {
  if (typeof message !== 'string' || !message.trim()) return false;
  const text = message.toLowerCase();
  return (
    text.includes('잠겼') ||
    text.includes('잠금') ||
    text.includes('locked') ||
    /5\s*회/.test(message) ||
    text.includes('too many') ||
    text.includes('attempt')
  );
};

/**
 * axios 등 로그인 실패 에러에서 계정 잠금 여부 추출.
 * body가 없으면 false.
 */
export const isLoginAccountLockedError = (error: unknown): boolean => {
  const err = error as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };

  const body = asRecord(err?.response?.data) as LoginErrorBody | null;
  if (body) {
    if (body.locked === true || body.accountLocked === true) {
      return true;
    }
    if (normalizeCode(body.status) === 'LOCKED') {
      return true;
    }
    if (
      isLockedCode(normalizeCode(body.code)) ||
      isLockedCode(normalizeCode(body.errorCode)) ||
      isLockedCode(normalizeCode(body.error))
    ) {
      return true;
    }
    if (looksLikeAccountLockedMessage(body.message)) {
      return true;
    }
  }

  // 네트워크·클라 오류 메시지만 있는 경우도 동일 휴리스틱
  if (looksLikeAccountLockedMessage(err?.message)) {
    return true;
  }

  return false;
};
